import { atom, selector } from 'recoil'
import _ from 'lodash'
import {
  AMDate,
  AMHourValue,
  AMMobilityAggregatedMethod,
  PatternsHourDatesResponse,
  PatternsHourResponse,
  QuarterPeriod,
  amMobilityAggregatedMethodEnum,
} from 'src/common-services'
import { alphamapClient, footfallClient } from 'src/clients'
import { DEFAULT_LRU_SIZE, keyFactory, retryAtom, uncachedSelector } from 'src/utils/recoil'
import { venueMatch } from 'src/atoms/search'
import { ifNotFound } from 'src/utils/ajax'
import { areaVisitsValidEndDate, multipolygon } from 'src/components/insights/common/visits/atoms/area'
import { DropdownOption } from 'src/components/dropdown'
import { Polygon } from '@turf/turf'

const key = keyFactory('insights-footfall')

export const patternsHourDates = selector<PatternsHourDatesResponse>({
  key: key('patterns-hour-dates'),
  get: ({ get }) => {
    get(retryAtom)
    return footfallClient.getPatternsHourDates()
  },
})

export const showPatternsHourDates = atom<boolean>({
  key: key('show-patterns-hour-dates'),
  default: true,
})

export const patternsHourDatesOptions = selector<DropdownOption[]>({
  key: key('patterns-hour-dates-options'),
  get: ({ get }) => {
    const patternsHourDatesValues = get(patternsHourDates)
    return patternsHourDatesValues.periods.map((period, index) => ({
      id: index.toString(),
      label: period.quarter,
    }))
  },
})

export const patternsHourInternalSelectedDateIndex = atom<number | undefined>({
  key: key('patterns-hour-internal-selected-date-index'),
  default: undefined,
})

export const patternsHourSelectedDateIndex = selector<number>({
  key: key('patterns-hour-selected-date-index'),
  get: ({ get }) => {
    const patternsHourDatesValue = get(patternsHourDates)
    const patternsHourInternalSelectedDateIndexValue = get(patternsHourInternalSelectedDateIndex)

    if (
      patternsHourInternalSelectedDateIndexValue !== undefined &&
      patternsHourDatesValue.periods[patternsHourInternalSelectedDateIndexValue]
    ) {
      return patternsHourInternalSelectedDateIndexValue
    }
    return patternsHourDatesValue.periods.length - 1
  },
})

export const patternsHourSelectedDate = selector<QuarterPeriod>({
  key: key('patterns-hour-selected-date'),
  get: ({ get }) => {
    const patternsHourDatesValue = get(patternsHourDates)
    const patternsHourSelectedDateIndexValue = get(patternsHourSelectedDateIndex)
    return patternsHourDatesValue.periods[patternsHourSelectedDateIndexValue]
  },
})

export const venuePatternsHourDate = atom<AMDate>({
  key: key('venue-patterns-hour-date'),
  default: areaVisitsValidEndDate,
})

const venuePatternsHour = uncachedSelector<PatternsHourResponse | undefined>({
  key: key('venue-patterns-hour'),
  get: ({ get }) => {
    get(retryAtom)
    const date = get(venuePatternsHourDate)
    const venue = get(venueMatch)
    if (!venue) {
      return undefined
    }
    return ifNotFound(
      footfallClient.getPatternsHour({
        start: date,
        venueId: venue.venueId,
      }),
      undefined
    )
  },
  lru: DEFAULT_LRU_SIZE,
})

export const areaHourlyAggregationMethod = atom<AMMobilityAggregatedMethod>({
  key: key('area-hourly-aggregation-method'),
  default: amMobilityAggregatedMethodEnum.avg,
})

export const areaPatternsHour = uncachedSelector<AMHourValue[] | undefined>({
  key: key('area-patterns-hour'),
  get: ({ get }) => {
    get(retryAtom)
    const date = get(venuePatternsHourDate)
    const shape = get(multipolygon)
    const polygon: Polygon = {
      type: 'Polygon',
      coordinates: shape.coordinates[0],
    }
    const method = get(areaHourlyAggregationMethod)

    return ifNotFound(
      alphamapClient.mobility.getPolygonVisitsByHour({
        date,
        polygon,
        method,
      }),

      undefined
    )
  },
  lru: DEFAULT_LRU_SIZE,
})

export const averageVisitsPerHour = uncachedSelector<number | undefined>({
  key: key('average-visits-per-hour'),
  get: ({ get }) => {
    const data = get(areaPatternsHour)
    if (!data?.length) {
      return undefined
    }

    const mean = _.mean(data.map((item) => item.value))
    return Math.round(mean)
  },
  lru: DEFAULT_LRU_SIZE,
})

export const peekHour = uncachedSelector<number | undefined>({
  key: key('peek-hour'),
  get: ({ get }) => {
    const data = get(areaPatternsHour)
    if (!data?.length) {
      return undefined
    }

    let max = 0
    let maxHour = 0

    data.forEach((item) => {
      if (item.value > max) {
        max = item.value
        maxHour = item.hour
      }
    })

    return maxHour
  },
  lru: DEFAULT_LRU_SIZE,
})

export const patternsHour = selector<PatternsHourResponse | undefined>({
  key: key('patterns-hour'),
  get: ({ get }) => {
    return get(venuePatternsHour)
  },
})
